import React from 'react';
import { observer } from 'mobx-react-lite';
import { useFullFormPageStore } from 'store/pages/enrollment/add-employees/full-form/pageStore';
import { DynamicFormModal } from '@insurancemenu/imenu-components-lib';
import SubscriberCard from 'components/common/election/cards/SubscriberCard';
import { toJS, action } from 'mobx';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { DependentsChoice, UpdatedPlanInfo } from 'types/electionService';
import { useFullEmployeeFormUrlParams } from 'global/hooks/useEmployeeFullFormUrlParams';
import { ElectionsTabId } from 'store/modules/full-employee-form/types';
import { PRODUCT_LINE_RATE_MODAL_TEST_ID, ELECTIONS_TAB_TEST_ID } from '../../constants';

const ElectionsTab: React.FC = observer(() => {
  const { electionsModule, electionsCardPresenter, subscriberBasicInfo, openProductLineRateForm } =
    useFullFormPageStore();

  const { subscriberId } = useFullEmployeeFormUrlParams();

  const subscriberElectionsDelegate = electionsModule.subscribers[0];
  if (!subscriberElectionsDelegate || !subscriberBasicInfo) {
    throw new Error('Cannot render ElectionsTab without elections and basic-info');
  }

  const pLineRateModalDelegate = electionsModule.productLineRateModalDelegate;
  const electionPresenter = subscriberElectionsDelegate.electionPresenter;

  const productLineRates = (electionsModule.statistics.subRates?.[electionPresenter.id] || [])
    .map((rate) => toJS(rate))
    .slice();
  const products = electionsModule.products.map((product) => toJS(product)).slice();
  const subscriberClasses = electionsModule.classesAvailableForSelection.map((cls) => toJS(cls)).slice();
  const requiredSubInfo = toJS(electionsModule.electionPresenter) ?? undefined;

  const updateProduct = action((selectedPlanInfo: UpdatedPlanInfo): void => {
    void subscriberElectionsDelegate.syncSelectedPlan(selectedPlanInfo);
  });

  const updateDependentsChoice = action((dependentsChoice: DependentsChoice): void => {
    void subscriberElectionsDelegate.syncDependents(dependentsChoice);
  });

  const updateClass = action((className: string): void => {
    void subscriberElectionsDelegate.syncClassAndResetProductSelection(className);
  });

  const onPLineRateChange = action((formId: string, productId: string): void => {
    void openProductLineRateForm(formId, productId);
  });

  const onShowDetails = action((productLineId: ProductLineId): void => {
    electionsModule.redirectToPortfolioDetails(
      productLineId,
      electionPresenter.employeeClass || '',
      `subscriberId=${subscriberId}&activeTabId=${ElectionsTabId.ELECTIONS}`,
    );
  });

  return (
    <div data-test-id={ELECTIONS_TAB_TEST_ID}>
      <SubscriberCard
        subscriber={electionsCardPresenter}
        productLineRates={productLineRates}
        products={products}
        subscriberClassesMap={{ employeeClasses: subscriberClasses }}
        requiredSubInfo={requiredSubInfo}
        callbacksMap={{
          updateProduct,
          updateDependentsChoice,
          updateClass,
          onPLineRateChange,
          onShowDetails,
        }}
      />
      <DynamicFormModal
        show={pLineRateModalDelegate.isVisible}
        onClose={pLineRateModalDelegate.close}
        formDelegate={pLineRateModalDelegate.dynamicFormDelegate}
        isEditable
        modalMaxWidth="90rem"
        formBodyMinHeight="20rem"
        data-test-id={PRODUCT_LINE_RATE_MODAL_TEST_ID}
      />
    </div>
  );
});

export default ElectionsTab;
